import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import type { DateClickArg } from '@fullcalendar/interaction';
import type { EventApi, EventClickArg, EventContentArg, EventDropArg, EventMountArg } from '@fullcalendar/core';
import type { EventResizeDoneArg } from '@fullcalendar/interaction';

import type { TemplateSlot, SlotKind } from '@/api/templates';
import type { Team } from '@/api/teams';
import type { Location } from '@/api/locations';
import { resolve as resolveTeamColor } from '@/lib/teamColor';
import { typeTone } from '@/lib/activityType';
import {
  availabilityBlockReason,
  type AvailabilityBlockReason,
  type BusinessHoursRule,
} from '@/components/schedule/ScheduleCalendar';

// TemplateCalendar renders a template's slots on a generic week canvas.
// "Generic" means the week has no real dates — only weekday names — so a
// slot like "Monday 17:00–18:30" displays the same way regardless of when
// the user is viewing the editor.
//
// Internally we still need real Dates for FullCalendar; we anchor on a
// fixed Monday in local time (TEMPLATE_REF_MONDAY) and translate slots
// to that week. Drag/resize callbacks translate back to (day_of_week,
// HH:MM) before they hit the API.
//
// Day headers show the weekday name only (no dates) via dayHeaderContent.

// Fixed reference Monday — local time 00:00 of a known Monday. The exact
// year doesn't matter; using one well in the past keeps it visually
// distinct from the live schedule view if a user ever bookmarks the URL.
// 2024-01-01 was a Monday.
const TEMPLATE_REF_MONDAY = new Date(2024, 0, 1, 0, 0, 0, 0);

export interface SlotChange {
  day_of_week: number; // 1..7 (Mon..Sun), ISO weekday
  start_time: string; // "HH:MM"
  end_time: string; // "HH:MM"
}

interface Props {
  slots: TemplateSlot[];
  // All slots in the template (unfiltered) — used for conflict detection so
  // that slots hidden by the team/location filter still block drops.
  allSlots?: TemplateSlot[];
  teams: Team[];
  locations: Location[];
  // When set (the user filtered the schedule to exactly one location),
  // greys out time slots outside the location's opening hours. Same
  // shape as ScheduleCalendar — daysOfWeek uses Sun=0..Sat=6.
  businessHours?: BusinessHoursRule[];
  onCreate: (suggestion: SlotChange) => void;
  onEdit: (slotId: string) => void;
  onReschedule: (slotId: string, change: SlotChange) => Promise<void>;
  // Fires when a click-to-create / drop / resize is rejected by the
  // availability gate. Parent renders a transient notice with the reason.
  onBlocked?: (reason: AvailabilityBlockReason) => void;
}

export function TemplateCalendar({
  slots,
  allSlots,
  teams,
  locations,
  businessHours,
  onCreate,
  onEdit,
  onReschedule,
  onBlocked,
}: Props) {
  const { t, i18n } = useTranslation();
  const ref = useRef<FullCalendar | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Drag-block tracking — see ScheduleCalendar for the rationale.
  // eventAllow swallows blocked drops before eventDrop fires, so we
  // stash the latest rejection reason here and surface it on drag end.
  const lastBlockedReasonRef = useRef<AvailabilityBlockReason | null>(null);

  // Pin the calendar to the reference Monday so navigation arrows can't
  // wander off the template week. We'd hide the toolbar entirely (header
  // is rendered by the parent), but FullCalendar still tracks an internal
  // "current date" — keep it on the reference week explicitly.
  useEffect(() => {
    const api = ref.current?.getApi();
    if (!api) return;
    api.gotoDate(TEMPLATE_REF_MONDAY);
  }, []);

  const teamsByID = useMemo(() => {
    const m = new Map<string, Team>();
    for (const tm of teams) m.set(tm.id, tm);
    return m;
  }, [teams]);

  const events = useMemo(() => {
    const locMap = new Map<string, Location>();
    for (const loc of locations) locMap.set(loc.id, loc);

    return slots.map((s) => {
      const loc = s.location_id ? locMap.get(s.location_id) : undefined;
      const zones = loc
        ? [...loc.zones].sort((x, y) => x.sort_order - y.sort_order)
        : [];
      const zoneIdx = s.zone_id ? zones.findIndex((z) => z.id === s.zone_id) : -1;
      const zoneTotal = zones.length;

      const team = s.team_id ? teamsByID.get(s.team_id) : undefined;
      // Slot.color is a per-slot override (rarely set). When present it
      // wins as the team-dot / border accent; otherwise fall back to the
      // team palette, and finally to the type-tone accent for slots
      // without a team. Background is ALWAYS the pale type-tint — same
      // scheme as ScheduleCalendar — so scanning training vs. match
      // works at a glance.
      const tone = typeTone(s.kind);
      const teamDot = s.color
        ? s.color
        : team
          ? resolveTeamColor(team.name, team.color).dot
          : '';
      const teamName = team?.name ?? '';

      return {
        id: s.id,
        title: s.title,
        start: slotDate(s.day_of_week, s.start_time),
        end: slotDate(s.day_of_week, s.end_time),
        backgroundColor: tone.bg,
        borderColor: teamDot || tone.accent,
        textColor: 'var(--color-foreground)',
        extendedProps: {
          team_id: s.team_id,
          team_name: teamName,
          activity_type: s.kind,
          tone_accent: tone.accent,
          team_dot: teamDot,
          location_id: s.location_id ?? null,
          zone_id: s.zone_id ?? null,
          location_name: loc?.name ?? null,
          zone_name: zoneIdx >= 0 ? (zones[zoneIdx]?.name ?? null) : null,
          zone_left: zoneIdx >= 0 && zoneTotal > 0 ? (zoneIdx / zoneTotal) * 100 : null,
          zone_right: zoneIdx >= 0 && zoneTotal > 0 ? ((zoneTotal - zoneIdx - 1) / zoneTotal) * 100 : null,
        },
      };
    });
  }, [slots, teamsByID, locations]);

  // Mirrors ScheduleCalendar's timeGridWeek card layout so the editor and
  // the schedule view read as the same component. Single layout — the
  // template calendar only ever renders timeGridWeek.
  const renderEventContent = useCallback((arg: EventContentArg) => {
    const ext = arg.event.extendedProps as {
      team_name?: string;
      location_name?: string | null;
      zone_name?: string | null;
      activity_type?: SlotKind;
      tone_accent?: string;
      team_dot?: string;
    };
    const teamName = ext.team_name ?? '';
    const locationParts = [ext.location_name, ext.zone_name].filter(Boolean) as string[];
    const typeLabel = ext.activity_type
      ? t(`schedule.activity.type.option.${ext.activity_type}`)
      : '';
    return (
      <div className="fc-event-stack flex h-full w-full flex-col gap-0.5 overflow-hidden px-2 py-1 text-[11px] leading-tight">
        <div className="fc-event-time-row flex items-baseline justify-between gap-2">
          <span className="truncate font-heading text-[10px] font-semibold tabular-nums text-foreground/60">
            {arg.timeText}
          </span>
          {typeLabel && (
            <span
              className="shrink-0 font-heading text-[9px] font-bold uppercase tracking-wider"
              style={{ color: ext.tone_accent }}
            >
              {typeLabel}
            </span>
          )}
        </div>
        {teamName && (
          <div className="flex items-center gap-1.5">
            {ext.team_dot && (
              <span
                className="inline-block size-2 shrink-0 rounded-full"
                style={{ background: ext.team_dot }}
                aria-hidden
              />
            )}
            <span className="truncate font-heading text-[12px] font-bold text-foreground">
              {teamName}
            </span>
          </div>
        )}
        {locationParts.length > 0 && (
          <div className="fc-event-meta truncate text-foreground/60">
            {locationParts.join(' · ')}
          </div>
        )}
      </div>
    );
  }, [t]);

  // Re-apply zone CSS to harneses whenever events change (e.g. when locations
  // load after the initial mount). handleEventDidMount only fires at mount.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const eventsMap = new Map(events.map((e) => [e.id, e.extendedProps]));
    for (const harness of container.querySelectorAll<HTMLElement>(
      '.fc-timegrid-event-harness[data-event-id]',
    )) {
      const id = harness.dataset.eventId;
      if (!id) continue;
      const props = eventsMap.get(id);
      if (props) applyZoneToHarness(harness, props);
    }
  }, [events]);

  function handleDateClick(arg: DateClickArg) {
    const start = new Date(arg.date);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    // Block click-to-create on greyed regions. Templates have no
    // dated exceptions, so the only gate is businessHours.
    const blocked = availabilityBlockReason(start, end, businessHours, undefined);
    if (blocked) {
      onBlocked?.(blocked);
      return;
    }
    onCreate(eventToChange(start, end));
  }

  function handleEventClick(arg: EventClickArg) {
    arg.jsEvent.preventDefault();
    onEdit(arg.event.id);
  }

  async function handleEventDrop(arg: EventDropArg) {
    if (!arg.event.start || !arg.event.end) {
      arg.revert();
      return;
    }
    const dropBlocked = availabilityBlockReason(arg.event.start, arg.event.end, businessHours, undefined);
    if (dropBlocked) {
      onBlocked?.(dropBlocked);
      arg.revert();
      return;
    }
    const conflict = hasSlotConflictReason(arg.event.id, arg.event.start, arg.event.end, arg.event.extendedProps, allSlots ?? slots);
    if (conflict) {
      onBlocked?.(conflict);
      arg.revert();
      return;
    }
    try {
      await onReschedule(arg.event.id, eventToChange(arg.event.start, arg.event.end));
    } catch {
      arg.revert();
    }
  }

  async function handleEventResize(arg: EventResizeDoneArg) {
    if (!arg.event.start || !arg.event.end) {
      arg.revert();
      return;
    }
    const resizeBlocked = availabilityBlockReason(arg.event.start, arg.event.end, businessHours, undefined);
    if (resizeBlocked) {
      onBlocked?.(resizeBlocked);
      arg.revert();
      return;
    }
    const conflict = hasSlotConflictReason(arg.event.id, arg.event.start, arg.event.end, arg.event.extendedProps, allSlots ?? slots);
    if (conflict) {
      onBlocked?.(conflict);
      arg.revert();
      return;
    }
    try {
      await onReschedule(arg.event.id, eventToChange(arg.event.start, arg.event.end));
    } catch {
      arg.revert();
    }
  }

  function handleEventDidMount(arg: EventMountArg) {
    const harness = arg.el.parentElement;
    if (!harness) return;
    (harness as HTMLElement).dataset.eventId = arg.event.id;
    applyZoneToHarness(harness as HTMLElement, arg.event.extendedProps as Record<string, unknown>);
  }

  return (
    <div ref={containerRef} className="schedule-calendar">
      <FullCalendar
        ref={ref}
        plugins={[timeGridPlugin, interactionPlugin]}
        initialView="timeGridWeek"
        initialDate={TEMPLATE_REF_MONDAY}
        firstDay={1 /* Monday */}
        locale={i18n.resolvedLanguage ?? 'en'}
        height="auto"
        slotMinTime="06:00:00"
        slotMaxTime="23:00:00"
        allDaySlot={false}
        editable
        eventStartEditable
        eventDurationEditable
        droppable={false}
        eventOverlap={(stillEvent, movingEvent) => {
          const reason = templateOverlapBlockReason(stillEvent, movingEvent);
          if (reason) lastBlockedReasonRef.current = reason;
          return reason === null;
        }}
        headerToolbar={false}
        dayHeaderContent={(arg) => {
          const fmt = new Intl.DateTimeFormat(i18n.resolvedLanguage ?? 'en', {
            weekday: 'long',
          });
          const txt = fmt.format(arg.date);
          return txt.charAt(0).toUpperCase() + txt.slice(1);
        }}
        events={events}
        {...(businessHours ? { businessHours } : {})}
        // eventAllow gates the live drag/resize preview so a slot can't
        // be dropped into a greyed region. Same predicate the handlers
        // use post-drop — eventAllow is the visual win, the handler is
        // the safety net.
        eventAllow={(drop) => {
          const reason = availabilityBlockReason(drop.start, drop.end, businessHours, undefined);
          lastBlockedReasonRef.current = reason;
          return reason === null;
        }}
        eventDragStart={() => {
          lastBlockedReasonRef.current = null;
        }}
        eventDragStop={() => {
          if (lastBlockedReasonRef.current) {
            onBlocked?.(lastBlockedReasonRef.current);
            lastBlockedReasonRef.current = null;
          }
        }}
        eventResizeStart={() => {
          lastBlockedReasonRef.current = null;
        }}
        eventResizeStop={() => {
          if (lastBlockedReasonRef.current) {
            onBlocked?.(lastBlockedReasonRef.current);
            lastBlockedReasonRef.current = null;
          }
        }}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        eventDidMount={handleEventDidMount}
        eventContent={renderEventContent}
      />
    </div>
  );
}

// slotDate maps (day_of_week, "HH:MM") → a Date inside the reference week.
// day_of_week is ISO 1..7 (Mon..Sun); the reference Monday is day 1.
function slotDate(dayOfWeek: number, hhmm: string): Date {
  const [h, m] = hhmm.split(':').map((n) => parseInt(n, 10));
  const d = new Date(TEMPLATE_REF_MONDAY);
  d.setDate(d.getDate() + (dayOfWeek - 1));
  d.setHours(h || 0, m || 0, 0, 0);
  return d;
}

// eventToChange maps a FullCalendar Date pair back to (day_of_week, HH:MM)
// so the parent can persist via updateSlot. ISO weekday: Monday = 1, Sunday = 7.
export function eventToChange(start: Date, end: Date): SlotChange {
  const jsDay = start.getDay(); // 0 = Sunday … 6 = Saturday
  const day_of_week = jsDay === 0 ? 7 : jsDay;
  return {
    day_of_week,
    start_time: hhmm(start),
    end_time: hhmm(end),
  };
}

function hhmm(d: Date): string {
  return [
    String(d.getHours()).padStart(2, '0'),
    String(d.getMinutes()).padStart(2, '0'),
  ].join(':');
}

function applyZoneToHarness(harness: HTMLElement, props: Record<string, unknown>) {
  const zone_left = props.zone_left as number | null;
  const zone_right = props.zone_right as number | null;
  if (zone_left === null || zone_right === null) {
    delete harness.dataset.zone;
    harness.style.removeProperty('--zone-left');
    harness.style.removeProperty('--zone-right');
    return;
  }
  harness.dataset.zone = '1';
  harness.style.setProperty('--zone-left', `${zone_left}%`);
  harness.style.setProperty('--zone-right', `${zone_right}%`);
}

// Returns the conflict reason during drag (one call per other-event
// overlap candidate). Same predicate as the old canEventsOverlap, just
// reasoned so the notice can tell the user which constraint they hit
// — especially valuable when the conflicting slot is hidden by the
// location/team filter and the "not allowed" cursor would otherwise
// leave them guessing.
function templateOverlapBlockReason(
  stillEvent: EventApi,
  movingEvent: EventApi | null,
): AvailabilityBlockReason | null {
  const stillTeam = (stillEvent.extendedProps.team_id as string | null) ?? null;
  const movingTeam = (movingEvent?.extendedProps.team_id as string | null) ?? null;
  if (stillTeam && movingTeam && stillTeam === movingTeam) return 'team_conflict';

  const stillLoc = (stillEvent.extendedProps.location_id as string | null) ?? null;
  const movingLoc = (movingEvent?.extendedProps.location_id as string | null) ?? null;
  if (!stillLoc || !movingLoc) return null;
  if (stillLoc !== movingLoc) return null;
  const stillZone = (stillEvent.extendedProps.zone_id as string | null) ?? null;
  const movingZone = (movingEvent?.extendedProps.zone_id as string | null) ?? null;
  if (stillZone !== null && movingZone !== null && stillZone !== movingZone) return null;
  return 'zone_conflict';
}

// Post-drop safety net for slots hidden by the team/location filter.
// Returns a reason for surfacing in the notice, or null when there's
// no conflict.
function hasSlotConflictReason(
  slotId: string,
  newStart: Date,
  newEnd: Date,
  extendedProps: Record<string, unknown>,
  allSlots: TemplateSlot[],
): AvailabilityBlockReason | null {
  const change = eventToChange(newStart, newEnd);
  const team_id = (extendedProps.team_id as string | null) ?? null;
  const location_id = (extendedProps.location_id as string | null) ?? null;
  const zone_id = (extendedProps.zone_id as string | null) ?? null;

  for (const s of allSlots) {
    if (s.id === slotId) continue;
    if (s.day_of_week !== change.day_of_week) continue;
    if (!(s.start_time < change.end_time && s.end_time > change.start_time)) continue;

    if (team_id && s.team_id === team_id) return 'team_conflict';

    if (!location_id || !s.location_id || s.location_id !== location_id) continue;
    const aZone = s.zone_id ?? null;
    if (zone_id !== null && aZone !== null && zone_id !== aZone) continue;
    return 'zone_conflict';
  }
  return null;
}
